import { useEffect, useMemo, useRef, useState } from 'react'
import { FiBold, FiHash, FiImage, FiLink, FiList, FiTrash2, FiUpload } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import {
  createNewsActivity,
  getNewsActivity,
  uploadNewsImage,
  updateNewsActivity,
} from '@domain/features/news/news-feature'
import type { ActivityExplore } from '@domain/models/activity-explore'
import type { NewsCategory } from '@domain/models/news-category'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useNewsCategories } from '@presentation/hooks/useNewsCategories'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { stripMarkdownToPlainText } from '@presentation/helpers/markdown'
import SeoHead from '@presentation/seo/SeoHead'

interface NewsDetailEditFormState {
  title: string
  newsCategoryId: string
  newsCategoryString: string
  mainCategoryId: string
  mainCategoryString: string
  image: string
  articleMarkdown: string
  sourceUrl: string
  importanceScore: string
}

const DEFAULT_IMPORTANCE_SCORE = 50
const MIN_IMPORTANCE_SCORE = 0
const MAX_IMPORTANCE_SCORE = 100

const EMPTY_FORM: NewsDetailEditFormState = {
  title: '',
  newsCategoryId: '',
  newsCategoryString: '',
  mainCategoryId: '',
  mainCategoryString: '',
  image: '',
  articleMarkdown: '',
  sourceUrl: '',
  importanceScore: String(DEFAULT_IMPORTANCE_SCORE),
}

interface ArticleEditResult {
  nextArticleMarkdown: string
  selectionStart: number
  selectionEnd: number
}

interface PendingArticleSelection {
  start: number
  end: number
  scrollTop: number
}

function wrapSelectionWithMarkdown(
  articleMarkdown: string,
  selectionStart: number,
  selectionEnd: number,
  prefix: string,
  suffix: string,
  fallbackText: string,
): ArticleEditResult {
  const selectedText = articleMarkdown.slice(selectionStart, selectionEnd)
  const innerText = selectedText.length > 0 ? selectedText : fallbackText
  const wrapped = `${prefix}${innerText}${suffix}`
  const nextArticleMarkdown = `${articleMarkdown.slice(0, selectionStart)}${wrapped}${articleMarkdown.slice(selectionEnd)}`
  const adjustedStart = selectionStart + prefix.length

  return {
    nextArticleMarkdown,
    selectionStart: adjustedStart,
    selectionEnd: adjustedStart + innerText.length,
  }
}

function prefixSelectedLinesWithMarkdown(
  articleMarkdown: string,
  selectionStart: number,
  selectionEnd: number,
  formatLine: (line: string, lineIndex: number) => string,
): ArticleEditResult {
  const lineStartIndex = articleMarkdown.lastIndexOf('\n', Math.max(0, selectionStart - 1)) + 1
  const lineBreakAfterSelection = articleMarkdown.indexOf('\n', selectionEnd)
  const lineEndIndex = lineBreakAfterSelection === -1 ? articleMarkdown.length : lineBreakAfterSelection
  const selectedBlock = articleMarkdown.slice(lineStartIndex, lineEndIndex)
  const lines = selectedBlock.split('\n')
  const formattedBlock = lines.map((line, index) => formatLine(line, index)).join('\n')
  const nextArticleMarkdown = `${articleMarkdown.slice(0, lineStartIndex)}${formattedBlock}${articleMarkdown.slice(lineEndIndex)}`

  return {
    nextArticleMarkdown,
    selectionStart: lineStartIndex,
    selectionEnd: lineStartIndex + formattedBlock.length,
  }
}

function insertMarkdownLink(
  articleMarkdown: string,
  selectionStart: number,
  selectionEnd: number,
): ArticleEditResult {
  const selectedText = articleMarkdown.slice(selectionStart, selectionEnd)
  const linkLabel = selectedText.length > 0 ? selectedText : 'link text'
  const linkUrl = 'https://example.com'
  const markdownLink = `[${linkLabel}](${linkUrl})`
  const nextArticleMarkdown = `${articleMarkdown.slice(0, selectionStart)}${markdownLink}${articleMarkdown.slice(selectionEnd)}`
  const urlStart = selectionStart + 1 + linkLabel.length + 2

  return {
    nextArticleMarkdown,
    selectionStart: urlStart,
    selectionEnd: urlStart + linkUrl.length,
  }
}

function toFormState(activity: ActivityExplore): NewsDetailEditFormState {
  return {
    title: activity.title ?? '',
    newsCategoryId: activity.newsCategoryId ?? '',
    newsCategoryString: activity.newsCategoryString ?? '',
    mainCategoryId: activity.mainCategoryId ?? '',
    mainCategoryString: activity.mainCategoryString ?? '',
    image: activity.image ?? '',
    articleMarkdown: activity.articleMarkdown ?? activity.article ?? '',
    sourceUrl: activity.sourceUrl ?? '',
    importanceScore:
      typeof activity.importanceScore === 'number' &&
      Number.isFinite(activity.importanceScore)
        ? String(activity.importanceScore)
        : String(DEFAULT_IMPORTANCE_SCORE),
  }
}

function normalizeOptional(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeUnknownString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function deriveMainCategory(category: NewsCategory | undefined): {
  mainCategoryId?: string
  mainCategoryString?: string
} {
  if (!category) {
    return {}
  }

  const rawCategory = category as Record<string, unknown>

  return {
    mainCategoryId:
      normalizeUnknownString(category.mainCategoryDocId) ??
      normalizeUnknownString(rawCategory['mainCategoryRefId']) ??
      normalizeUnknownString(rawCategory['main_category_ref_id']),
    mainCategoryString:
      normalizeUnknownString(rawCategory['mainCategoryString']) ??
      normalizeUnknownString(rawCategory['main_category_string']) ??
      normalizeUnknownString(rawCategory['mainCategoryName']) ??
      normalizeUnknownString(rawCategory['main_category_name']),
  }
}

export default function NewsDetailEditPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { activityId } = useParams<{ activityId?: string }>()
  const isEditMode = Boolean(activityId)
  const { user, loading: authLoading } = useAuthUser()
  const { categories } = useNewsCategories()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const articleTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const pendingArticleSelectionRef = useRef<PendingArticleSelection | null>(null)

  const [form, setForm] = useState<NewsDetailEditFormState | null>(() =>
    isEditMode ? null : EMPTY_FORM,
  )
  const [loading, setLoading] = useState(() => isEditMode)
  const [saving, setSaving] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isEditMode) {
      return
    }

    let cancelled = false

    async function loadActivity() {
      try {
        setLoading(true)
        setError(null)
        if (!activityId) {
          throw new Error('Activity id is missing.')
        }
        const activity = await getNewsActivity(activityId)
        if (!cancelled) {
          setForm(toFormState(activity))
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load activity.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadActivity()
    return () => {
      cancelled = true
    }
  }, [activityId, isEditMode])

  useEffect(() => {
    if (authLoading) {
      return
    }

    if (!user?.isAdmin) {
      navigate('/news', { replace: true })
    }
  }, [authLoading, navigate, user])

  const categoryNameById = useMemo(() => {
    return new Map(categories.map((category) => [category.uid, category.name]))
  }, [categories])
  const categoryById = useMemo(() => {
    return new Map(categories.map((category) => [category.uid, category]))
  }, [categories])

  const inputClassName =
    'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!'
  const sectionClassName = 'rounded-2xl border border-border-light bg-surface p-4'
  const articleToolbarButtonClassName =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-text-secondary transition-colors hover:border-blue hover:text-blue disabled:cursor-not-allowed disabled:opacity-60'

  useEffect(() => {
    const pendingSelection = pendingArticleSelectionRef.current
    const textarea = articleTextareaRef.current
    if (!pendingSelection || !textarea) {
      return
    }

    textarea.focus()
    textarea.setSelectionRange(pendingSelection.start, pendingSelection.end)
    textarea.scrollTop = pendingSelection.scrollTop
    pendingArticleSelectionRef.current = null
  }, [form?.articleMarkdown])

  const handleInputChange = (
    key: keyof NewsDetailEditFormState,
    value: string,
  ) => {
    setForm((current) => {
      if (!current) return current
      return { ...current, [key]: value }
    })
  }

  const applyArticleTransform = (
    transform: (
      articleMarkdown: string,
      selectionStart: number,
      selectionEnd: number,
    ) => ArticleEditResult,
  ) => {
    setForm((current) => {
      if (!current) {
        return current
      }

      const textarea = articleTextareaRef.current
      const fallbackSelection = current.articleMarkdown.length
      const selectionStart = textarea?.selectionStart ?? fallbackSelection
      const selectionEnd = textarea?.selectionEnd ?? fallbackSelection
      const scrollTop = textarea?.scrollTop ?? 0
      const result = transform(current.articleMarkdown, selectionStart, selectionEnd)

      pendingArticleSelectionRef.current = {
        start: result.selectionStart,
        end: result.selectionEnd,
        scrollTop,
      }

      return {
        ...current,
        articleMarkdown: result.nextArticleMarkdown,
      }
    })
  }

  const handleArticleBold = () => {
    applyArticleTransform((articleMarkdown, selectionStart, selectionEnd) => {
      const selectedText = articleMarkdown.slice(selectionStart, selectionEnd)
      if (!selectedText) {
        return wrapSelectionWithMarkdown(
          articleMarkdown,
          selectionStart,
          selectionEnd,
          '**',
          '**',
          'bold text',
        )
      }

      const leadingWhitespaceMatch = selectedText.match(/^\s*/)
      const trailingWhitespaceMatch = selectedText.match(/\s*$/)
      const leadingWhitespace = leadingWhitespaceMatch?.[0] ?? ''
      const trailingWhitespace = trailingWhitespaceMatch?.[0] ?? ''
      const trimmedSelection = selectedText.trim()
      if (!trimmedSelection) {
        return wrapSelectionWithMarkdown(
          articleMarkdown,
          selectionStart,
          selectionEnd,
          '**',
          '**',
          'bold text',
        )
      }

      const wrappedSelection = `${leadingWhitespace}**${trimmedSelection}**${trailingWhitespace}`
      const nextArticleMarkdown = `${articleMarkdown.slice(0, selectionStart)}${wrappedSelection}${articleMarkdown.slice(selectionEnd)}`
      const boldContentStart = selectionStart + leadingWhitespace.length + 2

      return {
        nextArticleMarkdown,
        selectionStart: boldContentStart,
        selectionEnd: boldContentStart + trimmedSelection.length,
      }
    })
  }

  const handleArticleBulletList = () => {
    applyArticleTransform((articleMarkdown, selectionStart, selectionEnd) =>
      prefixSelectedLinesWithMarkdown(
        articleMarkdown,
        selectionStart,
        selectionEnd,
        (line) => `- ${line}`,
      ))
  }

  const handleArticleNumberedList = () => {
    applyArticleTransform((articleMarkdown, selectionStart, selectionEnd) =>
      prefixSelectedLinesWithMarkdown(
        articleMarkdown,
        selectionStart,
        selectionEnd,
        (_line, index) => `${index + 1}. ${_line}`,
      ))
  }

  const handleArticleLink = () => {
    applyArticleTransform(insertMarkdownLink)
  }

  const handleArticleToolbarMouseDown = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
  }

  const handleCategoryChange = (nextCategoryId: string) => {
    const categoryName = categoryNameById.get(nextCategoryId) ?? ''
    const { mainCategoryId, mainCategoryString } = deriveMainCategory(
      categoryById.get(nextCategoryId),
    )
    setForm((current) => {
      if (!current) return current
      return {
        ...current,
        newsCategoryId: nextCategoryId,
        newsCategoryString: categoryName,
        mainCategoryId: mainCategoryId ?? '',
        mainCategoryString: mainCategoryString ?? '',
      }
    })
  }

  const openImagePicker = () => {
    if (saving || isUploadingImage) {
      return
    }
    fileInputRef.current?.click()
  }

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }

    setIsUploadingImage(true)
    setError(null)
    try {
      const uploadedImageUrl = await uploadNewsImage(file)
      setForm((current) => {
        if (!current) {
          return current
        }
        return {
          ...current,
          image: uploadedImageUrl,
        }
      })
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Failed to upload image.',
      )
    } finally {
      setIsUploadingImage(false)
    }
  }

  const removeImage = () => {
    setForm((current) => {
      if (!current) {
        return current
      }
      return {
        ...current,
        image: '',
      }
    })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!form) {
      return
    }

    const normalizedImportanceScore = form.importanceScore.trim()
    const importanceScore = Number(normalizedImportanceScore)
    if (
      normalizedImportanceScore.length === 0 ||
      !Number.isFinite(importanceScore) ||
      importanceScore < MIN_IMPORTANCE_SCORE ||
      importanceScore > MAX_IMPORTANCE_SCORE
    ) {
      setError('Newsletter importance score must be a number between 0 and 100.')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const normalizedImage = form.image.trim()
      const derivedMainCategory = deriveMainCategory(
        categoryById.get(form.newsCategoryId),
      )
      const normalizedMainCategoryId =
        normalizeOptional(form.mainCategoryId) ??
        normalizeOptional(derivedMainCategory.mainCategoryId ?? '')
      const normalizedMainCategoryString =
        normalizeOptional(form.mainCategoryString) ??
        normalizeOptional(derivedMainCategory.mainCategoryString ?? '')
      const trimmedArticleMarkdown = form.articleMarkdown.trim()
      const normalizedArticleMarkdown =
        trimmedArticleMarkdown.length > 0 ? trimmedArticleMarkdown : undefined
      const normalizedArticlePlainText = normalizedArticleMarkdown
        ? normalizeOptional(stripMarkdownToPlainText(normalizedArticleMarkdown))
        : undefined

      if (isEditMode && activityId) {
        await updateNewsActivity(activityId, {
          title: normalizeOptional(form.title),
          newsCategoryId: normalizeOptional(form.newsCategoryId),
          newsCategoryString: normalizeOptional(form.newsCategoryString),
          mainCategoryId: normalizedMainCategoryId,
          mainCategoryString: normalizedMainCategoryString,
          image: normalizedImage,
          articleMarkdown: normalizedArticleMarkdown ?? '',
          article: normalizedArticlePlainText ?? '',
          sourceUrl: normalizeOptional(form.sourceUrl),
          importanceScore,
        })
        AnalyticsFeature.trackNewsSubmit(screenPath, 'news_edit', true)
        navigate(`/news/${activityId}`)
      } else {
        const createdActivityId = await createNewsActivity({
          title: form.title.trim(),
          newsCategoryId: form.newsCategoryId,
          newsCategoryString: form.newsCategoryString,
          mainCategoryId: normalizedMainCategoryId,
          mainCategoryString: normalizedMainCategoryString,
          image: normalizedImage.length > 0 ? normalizedImage : undefined,
          articleMarkdown: normalizedArticleMarkdown,
          article: normalizedArticlePlainText,
          sourceUrl: normalizeOptional(form.sourceUrl),
          importanceScore,
        })
        AnalyticsFeature.trackNewsSubmit(screenPath, 'news_create', true)
        navigate(`/news/${createdActivityId}`)
      }
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : 'Failed to save changes.',
      )
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || loading) {
    return <LoadingSpinner className="min-h-[50vh]" />
  }

  if (error && !form) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <p className="text-lg font-semibold text-text-secondary">{error}</p>
        <Link
          to={isEditMode && activityId ? `/news/${activityId}` : '/news'}
          className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
        >
          &larr; Back
        </Link>
      </div>
    )
  }

  if (!form || !user?.isAdmin) {
    return <LoadingSpinner className="min-h-[50vh]" />
  }

  const cancelTarget = isEditMode && activityId ? `/news/${activityId}` : '/news'
  const parsedImportanceScore = Number(form.importanceScore)
  const importanceScoreBandLabel = !Number.isFinite(parsedImportanceScore)
    ? 'Invalid score'
    : parsedImportanceScore <= 24
      ? 'Very low'
      : parsedImportanceScore <= 49
        ? 'Low'
        : parsedImportanceScore <= 74
          ? 'Normal'
          : parsedImportanceScore <= 89
            ? 'High'
            : 'Critical / top story'

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <SeoHead
        title={isEditMode ? 'Edit News Article | Parkinson\'s Insights' : 'Create News Article | Parkinson\'s Insights'}
        canonicalPath={isEditMode && activityId ? `/news/${activityId}/edit` : '/news/create'}
        noindex
      />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-text-primary">
          {isEditMode ? 'Edit News Article' : 'Create News Article'}
        </h1>
        <Link
          to={cancelTarget}
          className="text-sm font-semibold text-primary hover:text-primary-light"
        >
          Cancel
        </Link>
      </div>

      {error && (
        <p className="mt-3 rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />

        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            Title
            <input
              value={form.title}
              onChange={(event) => handleInputChange('title', event.target.value)}
              className={`${inputClassName} mt-2`}
              required
            />
          </label>
        </section>

        <section className={sectionClassName}>
          <label className="block text-sm font-semibold text-text-secondary">
            News category
            <select
              value={form.newsCategoryId}
              onChange={(event) => handleCategoryChange(event.target.value)}
              className={`${inputClassName} mt-2`}
              required
            >
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.uid} value={category.uid}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
        </section>

        {user?.isAdmin ? (
          <section className={sectionClassName}>
            <label className="block text-sm font-semibold text-text-secondary">
              Newsletter importance score
              <input
                type="number"
                min={MIN_IMPORTANCE_SCORE}
                max={MAX_IMPORTANCE_SCORE}
                step={1}
                inputMode="numeric"
                value={form.importanceScore}
                onChange={(event) =>
                  handleInputChange('importanceScore', event.target.value)
                }
                className={`${inputClassName} mt-2`}
              />
            </label>
            <p className="mt-2 text-xs text-text-muted">
              Used to prioritize this article in newsletters. 50 is the default
              normal priority.
            </p>
            <input
              type="range"
              min={MIN_IMPORTANCE_SCORE}
              max={MAX_IMPORTANCE_SCORE}
              step={1}
              value={
                Number.isFinite(parsedImportanceScore)
                  ? Math.min(
                      MAX_IMPORTANCE_SCORE,
                      Math.max(MIN_IMPORTANCE_SCORE, parsedImportanceScore),
                    )
                  : DEFAULT_IMPORTANCE_SCORE
              }
              onChange={(event) =>
                handleInputChange('importanceScore', event.target.value)
              }
              className="mt-4 w-full accent-primary"
            />
            <p className="mt-2 text-xs font-medium text-text-secondary">
              Current level: {importanceScoreBandLabel}
            </p>
            <ul className="mt-3 space-y-1 text-xs text-text-muted">
              <li>0 = Not newsletter-worthy</li>
              <li>25 = Low importance</li>
              <li>50 = Normal/default importance</li>
              <li>75 = Important</li>
              <li>100 = Top/major story</li>
            </ul>
          </section>
        ) : null}

        <section className={sectionClassName}>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-text-secondary">Image added</p>
            {form.image ? (
              <button
                type="button"
                onClick={removeImage}
                disabled={isUploadingImage || saving}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-500 text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Remove image"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          {form.image ? (
            <div className="mt-3">
              <div className="relative overflow-hidden rounded-xl border border-border-light bg-border-light">
                <img
                  src={form.image}
                  alt="News article"
                  className="h-56 w-full object-cover"
                />
                {isUploadingImage ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/45 text-sm font-semibold text-white">
                    Uploading...
                  </div>
                ) : null}
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={openImagePicker}
                  disabled={isUploadingImage || saving}
                  className="inline-flex items-center gap-2 rounded-full border border-blue px-4 py-2 text-sm font-semibold text-blue transition-colors hover:bg-blue/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiUpload className="h-4 w-4" />
                  Upload new image
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={openImagePicker}
              disabled={isUploadingImage || saving}
              className="mt-3 flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-4 py-8 text-text-secondary transition-colors hover:border-blue hover:text-blue disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiImage className="h-6 w-6" />
              <span className="text-sm font-semibold">
                {isUploadingImage ? 'Uploading image...' : 'Add new image'}
              </span>
            </button>
          )}

          <label className="mt-4 block text-sm font-semibold text-text-secondary">
            Source URL
            <input
              value={form.sourceUrl}
              onChange={(event) => handleInputChange('sourceUrl', event.target.value)}
              className={`${inputClassName} mt-2`}
            />
          </label>
          <div className="mt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label htmlFor="news-article-markdown" className="text-sm font-semibold text-text-secondary">
                Article
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleArticleBold}
                  onMouseDown={handleArticleToolbarMouseDown}
                  className={articleToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Make article text bold"
                >
                  <FiBold className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleArticleBulletList}
                  onMouseDown={handleArticleToolbarMouseDown}
                  className={articleToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Add bullet list formatting"
                >
                  <FiList className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleArticleNumberedList}
                  onMouseDown={handleArticleToolbarMouseDown}
                  className={articleToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Add numbered list formatting"
                >
                  <FiHash className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleArticleLink}
                  onMouseDown={handleArticleToolbarMouseDown}
                  className={articleToolbarButtonClassName}
                  disabled={saving || isUploadingImage}
                  aria-label="Add markdown link"
                >
                  <FiLink className="h-4 w-4" />
                </button>
              </div>
            </div>
            <textarea
              id="news-article-markdown"
              ref={articleTextareaRef}
              value={form.articleMarkdown}
              onChange={(event) => handleInputChange('articleMarkdown', event.target.value)}
              rows={8}
              className={`${inputClassName} mt-2`}
            />
            <p className="mt-2 text-xs text-text-muted">
              Supports Markdown: bold, bullet lists, numbered lists, and links.
            </p>
          </div>
        </section>

        <div className="flex justify-center pt-2">
          <button
            type="submit"
            disabled={saving || isUploadingImage}
            className="inline-flex min-w-36 items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-light disabled:opacity-65"
          >
            {saving ? 'Saving...' : isUploadingImage ? 'Uploading image...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}
